import { useState } from 'react'
import { useOnboarding, useSendInvitations } from '../api/queries'
import type { OnboardingPerson } from '../api/types'
import { formatDateTime } from '../lib/format'
import { Avatar, Card } from './ui'

const statusText: Record<string, { label: string; style: React.CSSProperties }> = {
  never_logged_in: { label: 'Aldrig logget ind', style: { background: '#FEE2E2', color: '#991B1B' } },
  no_push: { label: 'Ingen beskeder på telefonen', style: { background: '#FEF3C7', color: '#92400E' } },
  ready: { label: 'Klar', style: { background: '#DCFCE7', color: '#166534' } }
}

/**
 * "Kom i gang" (beslutning 080): hvem mangler at tage systemet i brug, og en knap
 * der sender dem en invitation på SMS.
 *
 * Baggrunden: 10 af 14 medarbejdere havde aldrig været logget ind. Et system, ingen
 * er inde i, er ikke et system - og det kan man ikke se nogen steder i dag.
 *
 * Teksten skrives af serveren og vises her, før der sendes. Den, der ikke har et
 * telefonnummer, kan ikke vælges - nummeret kommer fra Microsoft 365.
 */
export function OnboardingCard() {
  const { data, isLoading, error } = useOnboarding()
  const invite = useSendInvitations()
  const [selected, setSelected] = useState<number[]>([])
  const [result, setResult] = useState<string | null>(null)

  if (isLoading || error || !data) return null

  const canPick = (person: OnboardingPerson) => !person.blocker && person.status !== 'ready'
  const pickable = data.people.filter(canPick)
  const preview = data.people.find((p) => p.userId === selected[0])

  function toggle(id: number) {
    setResult(null)
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  async function send() {
    setResult(null)
    try {
      const response = await invite.mutateAsync(selected)
      const failed = response.results.filter((r) => !r.sent)

      setResult(
        `Sendt til ${response.sent} af ${response.results.length}.` +
          (failed.length > 0
            ? ' Ikke sendt: ' + failed.map((r) => `${r.displayName} (${r.reason ?? 'ukendt fejl'})`).join('; ') + '.'
            : '')
      )
      setSelected([])
    } catch (err) {
      setResult(err instanceof Error ? err.message : 'Invitationen kunne ikke sendes.')
    }
  }

  return (
    <Card title="Kom i gang">
      <p className="small muted" style={{ marginTop: 0 }}>
        <strong>{data.neverLoggedIn}</strong> har aldrig været logget ind, <strong>{data.withoutPush}</strong> mangler
        beskeder på telefonen, og <strong>{data.ready}</strong> er klar. Vælg dem, der skal have en invitation på SMS
        med link og vejledning.
      </p>

      {!data.smsReady && (
        <div className="error-box" style={{ marginBottom: 10 }}>
          SMS er ikke slået til. Slå den til under <strong>SMS</strong> i adminpanelet, før der kan sendes
          invitationer.
        </div>
      )}

      {!data.link && (
        <div className="error-box" style={{ marginBottom: 10 }}>
          Serveren kender ikke sin egen adresse (<code>FRONTEND_URL</code>), så invitationen ville komme uden link.
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Medarbejder</th>
              <th>Status</th>
              <th>Seneste login</th>
              <th>Sidst inviteret</th>
            </tr>
          </thead>
          <tbody>
            {data.people.map((person) => (
              <tr key={person.userId} style={person.status === 'ready' ? { opacity: 0.6 } : undefined}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Vælg ${person.displayName}`}
                    checked={selected.includes(person.userId)}
                    disabled={!canPick(person) || !data.smsReady}
                    onChange={() => toggle(person.userId)}
                  />
                </td>
                <td>
                  <div className="row">
                    <Avatar user={{ id: person.userId, displayName: person.displayName, email: person.email, initials: person.initials, photoUrl: person.photoUrl }} />
                    {person.displayName}
                  </div>
                  {person.blocker && <div className="tiny warn">{person.blocker}</div>}
                </td>
                <td>
                  <span className="pill" style={statusText[person.status]?.style}>
                    {statusText[person.status]?.label ?? person.status}
                  </span>
                </td>
                <td className="small muted">{person.lastLoginAt ? formatDateTime(person.lastLoginAt) : 'Aldrig'}</td>
                <td className="small muted">{person.invitedAt ? formatDateTime(person.invitedAt) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="card card--flat" style={{ padding: 12, marginTop: 12 }}>
          <div className="tiny muted" style={{ marginBottom: 6 }}>
            Sådan lyder beskeden til {preview.displayName} ({preview.smsParts} SMS
            {preview.smsParts === 1 ? '' : "'er"}):
          </div>
          <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
            {preview.message}
          </div>
        </div>
      )}

      {result && (
        <div className="small" style={{ marginTop: 10 }}>
          {result}
        </div>
      )}

      <div className="btn-group" style={{ marginTop: 12 }}>
        <button
          className="btn btn--primary btn--sm"
          disabled={selected.length === 0 || invite.isPending || !data.smsReady}
          onClick={() => void send()}
        >
          {invite.isPending ? 'Sender ...' : `Send invitation til ${selected.length} valgte`}
        </button>
        {pickable.length > 0 && (
          <button
            className="btn btn--sm"
            disabled={!data.smsReady}
            onClick={() => {
              setResult(null)
              setSelected(pickable.map((p) => p.userId))
            }}
          >
            Vælg alle, der mangler ({pickable.length})
          </button>
        )}
        {selected.length > 0 && (
          <button className="btn btn--sm" onClick={() => setSelected([])}>
            Ryd valg
          </button>
        )}
      </div>
    </Card>
  )
}
